import { resolveProductPrice } from "../../../../../utils/productPrice";
import { forwardGa4EcommerceToPosthog } from "../../../../../utils/ga4Ecommerce";
import { isPublishableSku } from "../../../../../utils/productSku";

export function resolvePrice(special: unknown, price: unknown) {
  return resolveProductPrice(special, price);
}

export function parsePrice(val: unknown) {
  const n = Number(
    String(val ?? "")
      .toString()
      .replace(/[^0-9.\-]+/g, "")
  );
  return isNaN(n) ? 0 : n;
}

// A null or zero SKU is no fact (utils/productSku): the key is left out.
const skuField = (product: ProductLike) =>
  isPublishableSku(product.sku) ? { sku: product.sku } : {};

type ProductLike = {
  id?: string | number;
  sku?: string;
  name?: string;
  price?: unknown;
  special?: unknown;
  manufacturer?: { name?: unknown } | null;
};

export function useProductAnalytics() {
  const trackViewContent = (product: ProductLike) => {
    const rawSpecial = resolvePrice(product.special, product.price);
    const basePrice = parsePrice(product.price);
    const finalPrice = parsePrice(rawSpecial);

    if (window.fbq) {
      window.fbq("track", "ViewContent", {
        content_ids: [product.id],
        content_name: [product.name],
        content_type: "product",
        value: finalPrice,
        currency: "USD",
      });
    }
    if ((window as any).dataLayer) {
      const ecommerce = {
        currency: "USD",
        value: finalPrice,
        items: [
          {
            item_id: product.id,
            ...skuField(product),
            item_name: product.name,
            price: finalPrice,
            discount: Math.max(0, basePrice - finalPrice),
            quantity: 1,
          },
        ],
      };
      (window as any).dataLayer.push({ ecommerce: null });
      (window as any).dataLayer.push({
        event: "view_item",
        ecommerce,
      });
      // PostHog Product Viewed: the GA4 payload above plus the brand.
      forwardGa4EcommerceToPosthog("view_item", ecommerce, {
        brand: product.manufacturer?.name,
      });
    }
  };

  // Call only after the cart mutation is confirmed (see utils/cartAnalytics),
  // with the units that operation added.
  const trackAddToCart = (product: ProductLike, quantity = 1) => {
    const rawSpecial = resolvePrice(product.special, product.price);
    const basePrice = parsePrice(product.price);
    const finalPrice = parsePrice(rawSpecial);
    const value = finalPrice * quantity;

    if (window.fbq) {
      window.fbq("track", "AddToCart", {
        content_ids: [product.id],
        content_name: [product.name],
        content_type: "product",
        value,
        currency: "USD",
      });
    }
    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({ ecommerce: null });
      (window as any).dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          currency: "USD",
          value,
          items: [
            {
              item_id: product.id,
              ...skuField(product),
              item_name: product.name,
              price: finalPrice,
              discount: Math.max(0, basePrice - finalPrice),
              quantity,
            },
          ],
        },
      });
    }
  };

  const trackAddToWishlist = (product: ProductLike) => {
    const rawSpecial = resolvePrice(product.special, product.price);
    const basePrice = parsePrice(product.price);
    const finalPrice = parsePrice(rawSpecial);

    if (window.fbq) {
      window.fbq("track", "AddToWishlist", {
        content_ids: [product.id],
        content_name: [product.name],
        content_type: "product",
        value: finalPrice,
        currency: "USD",
      });
    }
    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({ ecommerce: null });
      (window as any).dataLayer.push({
        event: "add_to_wishlist",
        ecommerce: {
          currency: "USD",
          value: finalPrice,
          items: [
            {
              item_id: product.id,
              ...skuField(product),
              item_name: product.name,
              price: finalPrice,
              discount: Math.max(0, basePrice - finalPrice),
              quantity: 1,
            },
          ],
        },
      });
    }
  };

  return {
    trackViewContent,
    trackAddToCart,
    trackAddToWishlist,
  };
}
